/**
 * AuthorHighlight — V2.2 Block-Level Edition.
 *
 * Reads the `author_id`, `author_name`, `author_color` attributes from
 * each paragraph/heading node (set by the server via
 * ParagraphWithAuthor's schema or by REST writes via pycrdt
 * `XmlElement.attributes`). Renders a thin colored bar on the left
 * of each paragraph.
 *
 * Why this replaced the previous AuthorMark approach:
 *
 *   Mark-level authorship (V2.0) produced destructive Yjs merge ops
 *   that wiped agent-inserts. Block-level authorship is CRDT-stable —
 *   the paragraph's attribute lives on the node itself, no merge race
 *   with text inserts.
 *
 * Past contributors keep their identity because the attribute is part
 * of the doc, not ephemeral awareness.
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import type { Awareness } from 'y-protocols/awareness'
import { colorFromId } from './colorFromId'

interface AuthorHighlightOptions {
  awareness: Awareness | null
}

const AUTHOR_HIGHLIGHT_PLUGIN_KEY = new PluginKey('authorHighlight')
const REFRESH_META = 'authorHighlight:refresh'

interface AuthorInfo {
  id: string
  name: string
  color: string
}

export const AuthorHighlight = Extension.create<AuthorHighlightOptions>({
  name: 'authorHighlight',

  addOptions() {
    return { awareness: null }
  },

  addProseMirrorPlugins() {
    const options = this.options
    return [
      new Plugin({
        key: AUTHOR_HIGHLIGHT_PLUGIN_KEY,
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, oldDeco, _oldState, newState) => {
            const isRefresh = tr.getMeta(REFRESH_META) === true
            if (!tr.docChanged && !isRefresh) {
              return oldDeco.map(tr.mapping, tr.doc)
            }

            const liveIds = new Set<string>()
            if (options.awareness) {
              for (const [, state] of options.awareness.getStates() as Map<
                number,
                { user?: { id?: string } }
              >) {
                const id = state?.user?.id
                if (id) liveIds.add(id)
              }
            }

            const decos: Decoration[] = []
            // Issue #9 (rewrite): group consecutive paragraphs by
            // `commit_id` when present (server-stamped on each REST
            // doc_apply_text call — Issue #16). Falls back to
            // `author_id` for legacy and WS-typed paragraphs so a
            // human typing two paragraphs of prose still gets a
            // continuous stripe across their text instead of two
            // disconnected stubs.
            //
            // Each paragraph in the same group renders with collapsed
            // top margin + a padding-top large enough to make the
            // border-left box visually touch the previous paragraph's
            // border-left, so the strokes form ONE continuous vertical
            // line instead of N short stubs with gaps.
            let prevGroupKey: string | null = null
            newState.doc.descendants((node, pos) => {
              if (
                node.type.name !== 'paragraph' &&
                node.type.name !== 'heading'
              ) {
                return true
              }
              const attrs = node.attrs as {
                author_id?: string
                author_name?: string
                author_color?: string
                commit_id?: string | null
              }
              const text = node.textContent.trim()
              if (!attrs.author_id) {
                prevGroupKey = null
                return true
              }
              if (text === '') {
                prevGroupKey = null
                return true
              }
              // Group key: prefer the deterministic server commit_id;
              // for paragraphs without one (WS-typed by humans, legacy
              // pre-stamp content), fall back to author_id so adjacent
              // same-author lines still group naturally.
              const groupKey = attrs.commit_id
                ? `c:${attrs.commit_id}`
                : `a:${attrs.author_id}`
              const isContinuation = prevGroupKey === groupKey
              prevGroupKey = groupKey

              const name = attrs.author_name || 'Unknown'
              const color = colorFromId(attrs.author_id, name)
              const isLive = liveIds.has(attrs.author_id)

              // Make the border-left strokes of consecutive paragraphs
              // in the same group visually fuse into ONE continuous
              // vertical line. The collapse strategy:
              //   - continuation: margin-top = -0.75em (overlap into
              //     prev paragraph's bottom margin band), padding-top
              //     = 0.75em (push text back down so it doesn't
              //     visually overlap)
              //   - first-in-group: ordinary margin-top so it separates
              //     from the previous group's stripe
              // padding-bottom + margin-bottom unchanged: the next
              // continuation absorbs the gap above its border-left box.
              const groupStyle = isContinuation
                ? 'margin-top: -0.75em; padding-top: 0.75em;'
                : 'margin-top: 0.6em;'
              decos.push(
                Decoration.node(pos, pos + node.nodeSize, {
                  style: `border-left: 3px solid ${color}; padding-left: 8px; ${
                    isLive ? '' : 'opacity: 0.95;'
                  } ${groupStyle} transition: border-color 200ms;`,
                  'data-author': name,
                  'data-author-id': attrs.author_id,
                  'data-author-cont': isContinuation ? 'true' : 'false',
                  'data-group-key': groupKey,
                  'data-live': isLive ? 'true' : 'false',
                  title: isLive ? name : `${name} (not live)`,
                })
              )
              return true
            })

            return DecorationSet.create(newState.doc, decos)
          },
        },
        props: {
          decorations(state) {
            return AUTHOR_HIGHLIGHT_PLUGIN_KEY.getState(state)
          },
        },
        view(editorView) {
          const awareness = options.awareness
          const onAwareness = () => {
            const tr = editorView.state.tr.setMeta(REFRESH_META, true)
            editorView.dispatch(tr)
          }
          if (awareness) {
            awareness.on('update', onAwareness)
          }
          return {
            destroy() {
              if (awareness) {
                awareness.off('update', onAwareness)
              }
            },
          }
        },
      }),
    ]
  },
})

/**
 * Helper: collect distinct authors found in the doc by walking
 * paragraph/heading nodes and reading their author attrs.
 */
interface PMNode {
  type?: { name?: string }
  attrs?: Record<string, unknown>
  textContent?: string
  forEach?: (cb: (child: PMNode) => void) => void
}

export function collectAuthorsInDoc(doc: PMNode): AuthorInfo[] {
  const byId = new Map<string, AuthorInfo>()
  const walk = (node: PMNode) => {
    if (
      (node.type?.name === 'paragraph' || node.type?.name === 'heading') &&
      node.attrs
    ) {
      const id = node.attrs.author_id as string | undefined
      const text = (node.textContent ?? '').trim()
      if (id && text !== '' && !byId.has(id)) {
        const displayName = (node.attrs.author_name as string) || 'Unknown'
        byId.set(id, {
          id,
          name: displayName,
          // Recompute color from id+name; stored author_color is a
          // write-time snapshot that may use an obsolete palette.
          // Name as hint covers user:UUID → "Alexander Popovic".
          color: colorFromId(id, displayName),
        })
      }
    }
    node.forEach?.(walk)
  }
  walk(doc)
  return Array.from(byId.values())
}
